import {
  DropDownTree,
  DropDownTreeExpandEvent,
  DropDownTreeHandle,
  DropDownTreeProps,
  ListNoDataProps,
} from '@progress/kendo-react-dropdowns';
import { FC, useCallback, useEffect, useRef } from 'react';
import { initialExpandedState } from 'src/sections/view_checkbook/NestedExcelDropdown';
import { SetState } from 'src/types/generic';
import { expandedState } from 'src/utils/treeDataOperations';

const ListNoData = (props: ListNoDataProps) => (
  <div className="k-nodata">
    <div>
      <h4 style={{ fontSize: '1em' }}>
        <span className="k-icon k-i-warning" style={{ fontSize: '2.5em' }} />
        <br />
        <br />
        <p>No budget periods available</p>
        {props.children}
      </h4>
    </div>
  </div>
);

interface TreeDropDownProps extends DropDownTreeProps {
  setInitialExpandValue?: () => void;
  expanded: number[];
  setExpanded: SetState<number[]>;
  dropDownOpened: boolean;
  setDropDownOpened: SetState<boolean>;
  onBlurCloseAll?: boolean;
  valueRenderer?: (value: any) => JSX.Element;
}

const TreeDropDown: FC<TreeDropDownProps> = ({
  data = [],
  id,
  style,
  onChange,
  dataItemKey,
  placeholder,
  value,
  setInitialExpandValue,
  disabled,
  popupSettings,
  loading,
  expanded,
  setExpanded,
  textField,
  subItemsField,
  selectField,
  expandField,
  dropDownOpened,
  setDropDownOpened,
  item,
  onBlurCloseAll,
  valueRenderer,
  ...restProps
}) => {
  const dropDownRef = useRef<DropDownTreeHandle | null>(null);

  const onExpandChange = useCallback(
    (event: DropDownTreeExpandEvent) =>
      setExpanded(expandedState(event.item, dataItemKey, expanded)),
    [dataItemKey, expanded, setExpanded]
  );

  const dropDownEventHandler = useCallback(() => {
    if (dropDownOpened) {
      setInitialExpandValue?.();
      setDropDownOpened(false);
    } else {
      setDropDownOpened(true);
    }
  }, [dropDownOpened, setDropDownOpened, setInitialExpandValue]);

  const handleDropDownTab = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        setDropDownOpened(true);
      }
    },
    [setDropDownOpened]
  );

  useEffect(() => {
    const el = dropDownRef.current?.element;
    setInitialExpandValue?.();
    el?.addEventListener('click', dropDownEventHandler);
    return () => el?.removeEventListener('click', dropDownEventHandler);
  }, [dropDownEventHandler, setInitialExpandValue]);

  useEffect(() => {
    const el = dropDownRef.current?.element;
    el?.addEventListener('keypress', handleDropDownTab);
    return () => el?.removeEventListener('keypress', handleDropDownTab);
  }, [dropDownOpened, handleDropDownTab]);

  return (
    <DropDownTree
      ref={dropDownRef}
      id={id}
      {...restProps}
      style={style}
      data={data}
      value={value}
      placeholder={placeholder}
      textField={textField}
      dataItemKey={dataItemKey}
      selectField={selectField}
      expandField={expandField}
      onExpandChange={onExpandChange}
      item={item}
      subItemsField={subItemsField}
      listNoData={ListNoData}
      loading={loading}
      onBlur={
        onBlurCloseAll
          ? () => {
              setExpanded(initialExpandedState);
              setDropDownOpened(false);
            }
          : () => {
              setInitialExpandValue?.();
              setDropDownOpened(false);
            }
      }
      opened={dropDownOpened}
      onChange={onChange}
      disabled={disabled}
      popupSettings={popupSettings}
      valueHolder={valueRenderer}
    />
  );
};

export default TreeDropDown;
